import React from 'react';
import { Drawer, HStack, Icon, Portal, Tabs, Text, VStack } from '@chakra-ui/react';
import DrawerCloseButton from '@/components/ui/drawer-close-button.tsx';
import { LuFileText, LuGlobe, LuNotebookPen } from 'react-icons/lu';
import { AppliedCorrectionOut, DocumentWithDownloadUrl as Document, SourceOut, WebSourceOut } from '@/client';
import ChatResponseAnnotationsList from './ChatResponseAnnotationsList';
import ChatResponseCorrectionItem from './ChatResponseCorrectionItem';
import ChatResponseWebSourceItem from './ChatResponseWebSourceItem';
import EvidenceTriggerButton from './EvidenceTriggerButton';
import { useToastObstruction } from '@/hooks/ui/useToastObstruction.ts';
import { TOAST_EDGE_INSET_PX } from '@/utils/toastInsets';

export type EvidenceTab = 'records' | 'corrections' | 'web';

/** Chakra's `md` drawer size, which this drawer is pinned to below. */
const DRAWER_WIDTH_PX = 448;
const DRAWER_TOAST_INSET_PX = DRAWER_WIDTH_PX + TOAST_EDGE_INSET_PX;

interface ChatResponseSourcesProps {
	/** Cited sources (one per document) — drives the count + drawer. */
	sources: SourceOut[];
	webSources: WebSourceOut[];
	appliedCorrections: AppliedCorrectionOut[];
	documents: Document[];
	highlightedAnnotationIndex: number | null;
	registerAnnotationRef: (index: number, element: HTMLElement | null) => void;
	onOpenDocument: (doc: Document, pages?: number[]) => void;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	activeTab: EvidenceTab;
	onTabChange: (tab: EvidenceTab) => void;
	/** Clears any external return target before this component's own Drawer.Trigger opens it. */
	onInternalTriggerOpen: () => void;
	/** Returns the external evidence trigger that should regain focus when the drawer closes. */
	finalFocusEl: () => HTMLElement | null;
	/** Fired when the drawer's slide-in animation begins, so the owner can time a scroll to a source. */
	onOpenAnimationStart: () => void;
}

/**
 * "N sources" trigger that opens the answer's source documents in a side drawer,
 * instead of listing them inline beneath every answer.
 *
 */
const ChatResponseSources: React.FC<ChatResponseSourcesProps> = (props) => {
	const {
		sources,
		webSources,
		appliedCorrections,
		documents,
		highlightedAnnotationIndex,
		registerAnnotationRef,
		onOpenDocument,
		open,
		onOpenChange,
		activeTab,
		onTabChange,
		onInternalTriggerOpen,
		finalFocusEl,
		onOpenAnimationStart,
	} = props;

	useToastObstruction('right', open ? DRAWER_TOAST_INSET_PX : null);

	const recordsCount = sources.length;
	const correctionsCount = appliedCorrections.length;
	const webCount = webSources.length;
	const totalCount = recordsCount + correctionsCount + webCount;
	if (totalCount === 0) return null;

	const fallbackTab: EvidenceTab = recordsCount > 0 ? 'records' : correctionsCount > 0 ? 'corrections' : 'web';
	const activeTabHasContent =
		activeTab === 'records' ? recordsCount > 0 : activeTab === 'corrections' ? correctionsCount > 0 : webCount > 0;
	const visibleTab = activeTabHasContent ? activeTab : fallbackTab;
	const channelCount = Number(recordsCount > 0) + Number(correctionsCount > 0) + Number(webCount > 0);
	const showTabs = channelCount > 1;

	return (
		<Drawer.Root
			placement="end"
			size="md"
			open={open}
			finalFocusEl={finalFocusEl}
			onOpenChange={({ open }) => onOpenChange(open)}
		>
			<Drawer.Trigger asChild>
				<EvidenceTriggerButton
					display={{ base: 'inline-flex', md: recordsCount > 0 ? 'inline-flex' : 'none' }}
					onClick={() => {
						onInternalTriggerOpen();
						onTabChange(fallbackTab);
					}}
				>
					<HStack gap={0.5} aria-hidden>
						{recordsCount > 0 && <Icon as={LuFileText} boxSize={4} />}
						{webCount > 0 && (
							<Icon as={LuGlobe} boxSize={4} display={{ base: 'inline-block', md: 'none' }} />
						)}
						{correctionsCount > 0 && (
							<Icon as={LuNotebookPen} boxSize={4} display={{ base: 'inline-block', md: 'none' }} />
						)}
					</HStack>
					<Text as="span" display={{ base: 'inline', md: 'none' }}>
						{totalCount} {totalCount === 1 ? 'source' : 'sources'}
					</Text>
					<Text as="span" display={{ base: 'none', md: 'inline' }}>
						{recordsCount} {recordsCount === 1 ? 'source' : 'sources'}
					</Text>
				</EvidenceTriggerButton>
			</Drawer.Trigger>

			<Portal>
				<Drawer.Backdrop />
				<Drawer.Positioner>
					<Drawer.Content
						onAnimationStart={(event) => {
							// Ignore child + exit animations; only react to the content's own slide-in.
							if (event.target === event.currentTarget && event.currentTarget.dataset.state === 'open') {
								onOpenAnimationStart();
							}
						}}
					>
						<Drawer.Header>
							<HStack gap={1.5}>
								<Drawer.Title>Sources</Drawer.Title>
								<Text as="span" aria-hidden textStyle="label" color="fg.muted">
									·
								</Text>
								<Text as="span" textStyle="label" color="fg.muted">
									{totalCount}
								</Text>
							</HStack>
							<DrawerCloseButton />
						</Drawer.Header>
						<Drawer.Body px={0}>
							<Tabs.Root
								value={visibleTab}
								onValueChange={({ value }) => onTabChange(value as EvidenceTab)}
								variant="line"
								lazyMount
								unmountOnExit
							>
								{showTabs && (
									<Tabs.List px={{ base: 3, md: 6 }}>
										{recordsCount > 0 && (
											<Tabs.Trigger value="records">Records · {recordsCount}</Tabs.Trigger>
										)}
										{correctionsCount > 0 && (
											<Tabs.Trigger value="corrections">
												Corrections · {correctionsCount}
											</Tabs.Trigger>
										)}
										{webCount > 0 && <Tabs.Trigger value="web">Web · {webCount}</Tabs.Trigger>}
									</Tabs.List>
								)}

								<Tabs.Content value="records" px={{ base: 3, md: 6 }}>
									{recordsCount > 0 && (
										<ChatResponseAnnotationsList
											sources={sources}
											documents={documents}
											highlightedAnnotationIndex={highlightedAnnotationIndex}
											registerAnnotationRef={registerAnnotationRef}
											onOpenDocument={onOpenDocument}
										/>
									)}
								</Tabs.Content>

								<Tabs.Content value="corrections" px={{ base: 3, md: 6 }}>
									<VStack gap={3} align="stretch" mt={4}>
										{appliedCorrections.map((correction) => (
											<ChatResponseCorrectionItem key={correction.id} correction={correction} />
										))}
									</VStack>
								</Tabs.Content>

								<Tabs.Content value="web" px={{ base: 3, md: 6 }}>
									<VStack gap={3} align="stretch" mt={4}>
										{webSources.map((webSource) => (
											<ChatResponseWebSourceItem key={webSource.url} webSource={webSource} />
										))}
									</VStack>
								</Tabs.Content>
							</Tabs.Root>
						</Drawer.Body>
					</Drawer.Content>
				</Drawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
};

export default ChatResponseSources;
